import GenericTable from 'src/components/table/GenericTable'
import { IDocument } from 'src/generated-types/document'
import { Context, useMemo } from 'react'
import { GridColumns } from '@mui/x-data-grid-pro'
import { Flex } from 'src/styles/flexComponent'
import { FileUrlLoader } from 'src/components/FileUploader'
import { getFormattedDate, getThemeColor } from 'src/utils'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { AuthorizationProvider } from 'src/components/common/Authorization'
import { permissionValue } from 'src/enums/permissionValue'
import { AuthorizationType } from 'src/enums/auth'
import { getMainComponentName, getMooringName } from 'src/utils/function'
import { MooringDropdownDto } from 'src/generated-types/mooring-drop-down-dto'
import { useTranslation } from 'react-i18next'
import { invalidateDocument, useRestoreDocument } from 'src/api/document'
import { toast } from 'react-toastify'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { AxiosError } from 'axios'
import { Box, Typography, useTheme } from '@mui/material'
import { MUIThemeColors } from 'src/mui-theme'
import { DynamicTextTypography, DynamicTypography } from 'src/components/DynamicTextTypography'
import { getUserDetail } from 'src/utils/getUserDetails'
import { UserDetail } from '@lovoldsystem/core.client'
import { invalidateLocalityCache } from 'src/api/localities'
import { invalidateDocumentCategory } from 'src/api/users/document-category'
import { CenteredSpinner } from 'src/components/Spinner'
import { RestoreOutlined } from '@mui/icons-material'

interface ArchivedDocumentsTableProps {
  documents: IDocument[]
  moorings?: MooringDropdownDto[]
  hideColumns?: string[]
  customerId: EntityId
  context?: Context<any> | null
  isLoading?: boolean
}
const ArchivedDocumentsTable = ({
  documents,
  moorings,
  hideColumns,
  customerId,
  context,
  isLoading,
}: ArchivedDocumentsTableProps) => {
  const { t } = useTranslation(['document', 'common'])
  const { mutateAsync: restoreDocument } = useRestoreDocument(customerId)
  const { showConfirmDialog } = useDialogCtx()
  const theme = useTheme()

  const handleRestoreClick = async id => {
    showConfirmDialog(
      t('restore-document', { ns: 'document' }),
      t('areYouSureRestoreDocument', { ns: 'document' }),
      {
        acceptText: t('yes', { ns: 'common' }),
        cancelText: t('no', { ns: 'common' }),
      },
      async pop => {
        await restoreDocument(
          { id: id },
          {
            onSuccess: () => {
              toast.success(t('restored-document-successfully', { ns: 'document' }))
              invalidateDocument.getDocuments(customerId as EntityId)
              invalidateDocument.getArchivedDocuments(customerId as EntityId)
              invalidateLocalityCache.getLocalitiesWithInfoCount(customerId as EntityId)
              invalidateDocumentCategory.getDocumentCategoriesWithDocuments(customerId as EntityId)
              pop()
            },
            onError: (error: AxiosError) => {
              toast.error(t('restored-document-error', { ns: 'document' }))
            },
          },
        )
      },
      {
        Icon: (
          <RestoreOutlined
            sx={{ height: '43px', width: '48px', color: getThemeColor(theme, MUIThemeColors.errorMain) }}
          />
        ),
        iconBGColor: '#D32F2F4D',
        buttonTextColor: theme.palette.white[theme.palette.mode],
        buttonBGColor: theme.palette.errorMain[theme.palette.mode],
      },
    )
  }
  const STATIC_COLUMNS: GridColumns = useMemo(
    () => [
      {
        field: 'file',
        headerName: t('file', { ns: 'common' }),
        maxWidth: 0,
        sortable: false,
        hide: hideColumns?.includes('file'),
        renderCell: params => {
          const document = params.row as IDocument
          return (
            <Flex.Column justifyContent={'center'} alignItems={'center'} width={'32px'} height={'32px'}>
              <FileUrlLoader contentTypeValue={params?.row?.fileContentType} fileUrl={document.fileUrl} openOnClick />
            </Flex.Column>
          )
        },
      },
      {
        field: 'title',
        headerName: t('title', { ns: 'common' }),
        flex: 1.5,
        sortable: true,
        renderCell: ({ row }) => row.title,
        hide: hideColumns?.includes('title'),
      },
      {
        field: 'placement',
        headerName: t('original-path', { ns: 'document' }),
        minWidth: 200,
        flex: 1.5,
        hide: hideColumns?.includes('placement'),
        renderCell: ({ row }) => {
          const placement = Object.entries(JSON.parse(row.placement)).map(([key, value]) => {
            if (key === 'mooring' && value != null) {
              return [key, getMooringName(value as string)]
            }
            if (key === 'mainComponent' && value != null) {
              return [key, getMainComponentName(value as string)]
            }
            return [key, value]
          })
          const placementArray = placement
            .filter(([, item]) => item)
            .map(([, option]) => option)
            .reverse()
          return (
            <Box width={'100%'}>
              <DynamicTypography text={placementArray.join(' -> ')} variant="body1" fontWeight={400} />
            </Box>
          )
        },
      },
      {
        field: 'createdAt',
        headerName: t('uploaded-date', { ns: 'common' }),
        flex: 1,
        sortable: true,
        renderCell: ({ row }) => getFormattedDate(row.createdAt),
        hide: hideColumns?.includes('createdAt'),
      },
      {
        field: 'deletedAt',
        headerName: t('deleted-info', { ns: 'document' }),
        flex: 1,
        sortable: true,
        hide: hideColumns?.includes('deletedAt'),
        renderCell: ({ row }) => {
          const document = row as IDocument
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body2" fontWeight={400}>
                {document.deletedAt ? getFormattedDate(document.deletedAt) : 'N/A'}
              </Typography>
              <DynamicTextTypography
                variant="body2"
                fontWeight={400}
                text={t('by', { ns: 'common' }) + ' ' + getUserDetail(document.deletedByUser, UserDetail.FullName)}
                maxLength={25}
                color={getThemeColor(theme, MUIThemeColors.secondaryMain)}
              />
            </Box>
          )
        },
      },
      {
        field: 'mooring',
        headerName: t('mooring', { ns: 'common' }),
        sortable: true,
        flex: 1,
        hide: hideColumns?.includes('mooring'),
        valueGetter: params => {
          const document = params.row as IDocument
          const mooring = document.mooringId != null ? moorings?.find(m => m.id == document.mooringId) : null
          return mooring != null ? getMooringName(mooring.name) : '\uFFFF N/A'
        },
        renderCell: params => {
          const document = params.row as IDocument
          const mooring = document.mooringId != null ? moorings?.find(m => m.id == document.mooringId) : null
          return mooring != null ? getMooringName(mooring.name) : 'N/A'
        },
      },
    ],
    [],
  )

  const columns: GridColumns = useMemo(
    () => [
      ...STATIC_COLUMNS,
      {
        field: 'action',
        headerName: t('actions', { ns: 'common' }),
        type: 'actions',
        align: 'center',
        hide: hideColumns?.includes('action'),
        width: 100,
        renderCell: ({ row, id }) => {
          return !row.isGlobal ? (
            <Flex.Row alignItems={'center'} gap={1} marginBottom={'2px'}>
              <AuthorizationProvider
                permissions={[permissionValue.ClientAdminPermission]}
                type={AuthorizationType.Disabled}
                Context={context}
              >
                <GridActionButton
                  buttonText={t('restore', { ns: 'common' })}
                  shadowWeight={0}
                  onClick={() => handleRestoreClick(row.id)}
                  disabled={row?.isAttachment}
                  border={0}
                  sx={{ backgroundColor: 'transparent' }}
                />
              </AuthorizationProvider>
            </Flex.Row>
          ) : (
            <></>
          )
        },
      },
    ],
    [],
  )
  if (isLoading) return <CenteredSpinner height={200} />
  return (
    <GenericTable
      id="archived-documents"
      data={documents}
      columns={columns}
      getRowHeight={() => 44}
      autoHeight={true}
    />
  )
}

export default ArchivedDocumentsTable
